import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react'
import jwtDecode from 'jwt-decode'
import { useSearchParams } from 'react-router-dom'
import { useUserPreferences } from '../../../contexts/UserPreferencesContext'

type AutomationTab = 'automations' | 'connections' | 'integration' | 'documentation'
type ViewMode = 'board' | 'table' | 'sector' | 'service'
export type SortBy =
  | 'phase'
  | 'phase_desc'
  | 'name_asc'
  | 'name_desc'
  | 'recent'
  | 'oldest'
  | 'urgency'
  | 'deadline'
  | 'deadline_desc'
  | 'tags_asc'
  | 'tags_desc'
  | 'details_asc'
  | 'details_desc'

interface DecodedToken {
  sub: number
  tenantId: number
  exp: number
  iat: number
}

export type DateFilterField = 'deadline' | 'opStart'

interface PipelineFilters {
  keyword: string
  urgency: boolean
  dueDate: {
    notExpired: boolean
    nearExpiry: boolean
    expired: boolean
    specificDate: string
    from: string
    to: string
  }
  dateFilter: {
    field: DateFilterField
    from: string
    to: string
  }
  assignedUsers: number[]
  tags: number[]
}

interface FilterUser {
  id: number
  name: string
  picture?: string
}

interface FilterTag {
  id: number
  name: string
  color?: string
}

export type ColumnSort = { id: string; desc: boolean }

interface PipelineContextType {
  isConfiguringPipeline: boolean
  setIsConfiguringPipeline: React.Dispatch<React.SetStateAction<boolean>>
  viewOrEditTagsModalIsOpen: boolean
  setViewOrEditTagsModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  automationsPageIsOpen: boolean
  setAutomationsPageIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  activeAutomationTab: AutomationTab
  setActiveAutomationTab: React.Dispatch<React.SetStateAction<AutomationTab>>
  pipelineName: string
  setPipelineName: React.Dispatch<React.SetStateAction<string>>
  isCreatingAutomation: boolean
  setIsCreatingAutomation: React.Dispatch<React.SetStateAction<boolean>>
  editingAutomationId: number | null
  setEditingAutomationId: React.Dispatch<React.SetStateAction<number | null>>
  tenantId: number
  setTenantId: React.Dispatch<React.SetStateAction<number>>
  viewMode: ViewMode
  setViewMode: React.Dispatch<React.SetStateAction<ViewMode>>
  sortBy: SortBy | ''
  setSortBy: React.Dispatch<React.SetStateAction<SortBy | ''>>
  columnSort: ColumnSort | null
  setColumnSort: React.Dispatch<React.SetStateAction<ColumnSort | null>>
  filters: PipelineFilters
  setFilters: React.Dispatch<React.SetStateAction<PipelineFilters>>
  availableUsers: FilterUser[]
  setAvailableUsers: React.Dispatch<React.SetStateAction<FilterUser[]>>
  availableTags: FilterTag[]
  setAvailableTags: React.Dispatch<React.SetStateAction<FilterTag[]>>
  showArchived: boolean
  setShowArchived: React.Dispatch<React.SetStateAction<boolean>>
  showInactiveCards: boolean
  setShowInactiveCards: React.Dispatch<React.SetStateAction<boolean>>
  isAddingTableCard: boolean
  setIsAddingTableCard: React.Dispatch<React.SetStateAction<boolean>>
  moduleType: string | null
  subModule?: 'steel' | 'fashion'
  isModuleView: boolean
  contextPipelineId: number | null
  setContextPipelineId: React.Dispatch<React.SetStateAction<number | null>>
}

const PipelineContext = createContext<PipelineContextType | undefined>(undefined)

export const usePipelineContext = () => {
  const context = useContext(PipelineContext)
  if (!context) {
    throw new Error('usePipelineContext must be used within a PipelineProvider')
  }
  return context
}

export const usePipelineContextSafe = () => {
  return useContext(PipelineContext) ?? null
}

interface PipelineProviderProps {
  children: ReactNode
  moduleType?: string | null
  subModule?: 'steel' | 'fashion'
}

export const PipelineProvider: React.FC<PipelineProviderProps> = ({ children, moduleType = null, subModule }) => {
  const [searchParams] = useSearchParams()
  const pipelineId = searchParams.get('pipelineId')
  const { getPreference, setPreference, isLoading: isPrefsLoading } = useUserPreferences()

  const getStorageKey = () => {
    if (moduleType) {
      return `pipeline-view-mode:${moduleType}:${subModule || 'default'}`
    }
    if (pipelineId) {
      return `pipeline-view-mode:${pipelineId}`
    }
    return 'pipeline-view-mode:global'
  }

  const getFiltersStorageKey = () => {
    if (moduleType) {
      return `pipeline-filters:${moduleType}:${subModule || 'default'}`
    }
    if (pipelineId) {
      return `pipeline-filters:${pipelineId}`
    }
    return 'pipeline-filters:global'
  }

  const isProductionModule = moduleType === 'production'

  const getInitialViewMode = (): ViewMode => {
    const saved = getPreference<ViewMode | undefined>(getStorageKey())
    if (saved && ['board', 'table', 'sector', 'service'].includes(saved)) {
      return saved
    }
    return isProductionModule ? 'sector' : 'board'
  }

  const getInitialFilters = (): PipelineFilters => {
    const saved = getPreference<PipelineFilters | undefined>(getFiltersStorageKey())
    if (saved) {
      return saved
    }
    return {
      keyword: '',
      urgency: false,
      dueDate: {
        notExpired: false,
        nearExpiry: false,
        expired: false,
        specificDate: '',
        from: '',
        to: '',
      },
      dateFilter: {
        field: 'deadline',
        from: '',
        to: '',
      },
      assignedUsers: [],
      tags: []
    }
  }

  const getInitialSortBy = (): SortBy | '' => 'phase'

  const [isConfiguringPipeline, setIsConfiguringPipeline] = useState(false)
  const [viewOrEditTagsModalIsOpen, setViewOrEditTagsModalIsOpen] = useState<boolean>(false)
  const [automationsPageIsOpen, setAutomationsPageIsOpen] = useState<boolean>(false)
  const [activeAutomationTab, setActiveAutomationTab] = useState<AutomationTab>('automations')
  const [pipelineName, setPipelineName] = useState<string>('')
  const [isCreatingAutomation, setIsCreatingAutomation] = useState<boolean>(false)
  const [editingAutomationId, setEditingAutomationId] = useState<number | null>(null)
  const [tenantId, setTenantId] = useState<number>(0)
  const [viewMode, setViewMode] = useState<ViewMode>(getInitialViewMode)
  const [sortBy, setSortBy] = useState<SortBy | ''>(getInitialSortBy)
  const [columnSort, setColumnSort] = useState<ColumnSort | null>(null)
  const [filters, setFilters] = useState<PipelineFilters>(getInitialFilters)
  const [availableUsers, setAvailableUsers] = useState<FilterUser[]>([])
  const [availableTags, setAvailableTags] = useState<FilterTag[]>([])
  const [showArchived, setShowArchived] = useState<boolean>(false)
  const [showInactiveCards, setShowInactiveCards] = useState<boolean>(false)
  const [isAddingTableCard, setIsAddingTableCard] = useState<boolean>(false)
  const [contextPipelineId, setContextPipelineId] = useState<number | null>(null)

  const isModuleView = !!moduleType

  useEffect(() => {
    if (isPrefsLoading) return
    const key = getStorageKey()
    const saved = getPreference<ViewMode | undefined>(key)
    if (saved && ['board', 'table', 'sector', 'service'].includes(saved)) {
      setViewMode(saved)
    }
  }, [isPrefsLoading, moduleType, subModule, pipelineId])

  useEffect(() => {
    if (isPrefsLoading) return
    const key = getFiltersStorageKey()
    const saved = getPreference<PipelineFilters | undefined>(key)
    if (saved) {
      setFilters(saved)
    }
  }, [isPrefsLoading, moduleType, subModule, pipelineId])

  useEffect(() => {
    if (isPrefsLoading) return
    setPreference(getStorageKey(), viewMode)
  }, [viewMode, moduleType, subModule, pipelineId, isPrefsLoading])

  useEffect(() => {
    if (isPrefsLoading) return
    setPreference(getFiltersStorageKey(), filters)
  }, [filters, moduleType, subModule, pipelineId, isPrefsLoading])

  useEffect(() => {
    if (showArchived) {
      setShowInactiveCards(false)
    }
  }, [showArchived])

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      try {
        const decoded = jwtDecode<DecodedToken>(token)
        console.debug('[PipelineContext] Token decoded, tenantId:', decoded.tenantId)
        setTenantId(decoded.tenantId)
      } catch (error) {
        console.error('[PipelineContext] Erro ao decodificar token:', error)
      }
    }
  }, [])

  return (
    <PipelineContext.Provider
      value={{
        isConfiguringPipeline,
        setIsConfiguringPipeline,
        viewOrEditTagsModalIsOpen,
        setViewOrEditTagsModalIsOpen,
        automationsPageIsOpen,
        setAutomationsPageIsOpen,
        activeAutomationTab,
        setActiveAutomationTab,
        pipelineName,
        setPipelineName,
        isCreatingAutomation,
        setIsCreatingAutomation,
        editingAutomationId,
        setEditingAutomationId,
        tenantId,
        setTenantId,
        viewMode,
        setViewMode,
        sortBy,
        setSortBy,
        columnSort,
        setColumnSort,
        filters,
        setFilters,
        availableUsers,
        setAvailableUsers,
        availableTags,
        setAvailableTags,
        showArchived,
        setShowArchived,
        showInactiveCards,
        setShowInactiveCards,
        isAddingTableCard,
        setIsAddingTableCard,
        moduleType,
        subModule,
        isModuleView,
        contextPipelineId,
        setContextPipelineId,
      }}
    >
      {children}
    </PipelineContext.Provider>
  )
}
